import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Trash2, AlertTriangle, UserX } from "lucide-react";
import { client } from "@/client";
import { formatDateTime } from "@/lib/format-time";

interface DeletedUser {
  id: number;
  clerkId: string;
  email: string;
  name: string | null;
  deletedAt: string;
}

interface DeletionPreview {
  user: {
    id: number;
    email: string;
    name: string | null;
    deletedAt: string;
  };
  relatedData: {
    checkIns: number;
    pointTransactions: number;
    eventsCreated: number;
  };
  deletionPlan: {
    willDelete: string[];
    willNullify: string[];
  };
}

export function DeletedUsersSection() {
  const { getToken } = useAuth();
  const [deletedUsers, setDeletedUsers] = useState<DeletedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<DeletedUser | null>(null);
  const [deletionPreview, setDeletionPreview] = useState<DeletionPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchDeletedUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchDeletedUsers() {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await client.api.users.deleted.$get({}, { headers });

      if (!res.ok) {
        console.error("Failed to fetch deleted users:", res.status);
        setDeletedUsers([]);
        return;
      }

      const data = (await res.json()) as { users: DeletedUser[] };
      setDeletedUsers(data.users ?? []);
    } catch (err) {
      console.error("Failed to fetch deleted users:", err);
      setDeletedUsers([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function openDeleteDialog(user: DeletedUser) {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
    setIsLoadingPreview(true);
    setDeletionPreview(null);

    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await (client.api.users[":id"]["deletion-preview"] as any).$get(
        { param: { id: String(user.id) } },
        { headers }
      );
      const data = (await res.json()) as DeletionPreview;
      setDeletionPreview(data);
    } catch (err) {
      console.error("Failed to fetch deletion preview:", err);
    } finally {
      setIsLoadingPreview(false);
    }
  }

  async function permanentlyDeleteUser() {
    if (!selectedUser) return;

    setIsDeleting(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await (client.api.users[":id"].permanent as any).$delete(
        { param: { id: String(selectedUser.id) } },
        { headers }
      );

      if (res.ok) {
        setDeleteDialogOpen(false);
        setSelectedUser(null);
        setDeletionPreview(null);
        fetchDeletedUsers();
      }
    } catch (err) {
      console.error("Failed to delete user:", err);
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <UserX className="h-5 w-5" />
          삭제된 유저 관리
        </CardTitle>
        <CardDescription>
          Soft delete된 유저를 영구 삭제할 수 있습니다. 이 작업은 되돌릴 수 없습니다.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {deletedUsers.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            삭제된 유저가 없습니다.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>이름</TableHead>
                <TableHead>이메일</TableHead>
                <TableHead>삭제일</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deletedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user.name ?? "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {user.email}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(user.deletedAt)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => openDeleteDialog(user)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      영구 삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                유저 영구 삭제
              </DialogTitle>
              <DialogDescription>
                이 작업은 되돌릴 수 없습니다. 삭제 전 영향받는 데이터를 확인하세요.
              </DialogDescription>
            </DialogHeader>

            {isLoadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : deletionPreview ? (
              <div className="space-y-4">
                {/* User Info */}
                <div className="bg-muted rounded-lg p-4">
                  <p className="font-medium">{deletionPreview.user.name ?? "이름 없음"}</p>
                  <p className="text-sm text-muted-foreground">{deletionPreview.user.email}</p>
                </div>

                {/* Deletion Preview */}
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>삭제될 데이터</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>체크인 기록: {deletionPreview.relatedData.checkIns}건</li>
                      <li>포인트 이력: {deletionPreview.relatedData.pointTransactions}건</li>
                      <li>유저 정보</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                {deletionPreview.relatedData.eventsCreated > 0 && (
                  <Alert>
                    <AlertTitle>수정될 데이터</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc list-inside mt-2">
                        <li>
                          생성한 이벤트 {deletionPreview.relatedData.eventsCreated}개의
                          생성자 정보가 "알 수 없음"으로 변경됩니다.
                        </li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            ) : (
              <p className="text-center py-4 text-muted-foreground">
                미리보기를 불러올 수 없습니다.
              </p>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                취소
              </Button>
              <Button
                variant="destructive"
                onClick={permanentlyDeleteUser}
                disabled={isDeleting || isLoadingPreview || !deletionPreview}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    삭제 중...
                  </>
                ) : (
                  "영구 삭제 확인"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
